import React, { Fragment } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { GRANT_SHAPE, TAXONOMY_MAP } from 'config/prop-types';

import GrantsListItem from 'components/grantsListItem';

import './grantsList.scss';

export default class GrantsList extends React.PureComponent {
  /**
   * Creates and manages the display of the sort button.
   * @param {string} type - the filter for which the button is being made (ex. name, amount, date).
   * @param {string} label - text to display on the button.
   * @returns {object} - JSX for the button.
   */
  renderSortButton(type, options) {
    const {
      handleSortClick,
      sortDirection,
      sortField,
    } = this.props;

    const asc = type === sortField && 1 === sortDirection;
    const desc = type === sortField && 1 > sortDirection;
    const leftAlign = 'strategy' === type;

    return (
      <div className="grant-item__sort">
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label className="grant-item__sort-label" htmlFor="grant-items-sort">
          {type}
        </label>
        <select
          aria-label={type}
          className={classNames(
            'grant-item__details sort',
            {
              leftAlign,
              asc,
              desc,
            }
          )}
          id="grant-items-sort"
          onChange={(e) => handleSortClick(e.target.value)}
        >
          {options.map(({ label, value }) => (
            <option
              value={value}
            >
              {label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  /**
   * Creates and manages the display of the sort button.
   * @param {string} type - the filter for which the button is being made (ex. name, amount, date).
   * @param {string} label - text to display on the button.
   * @returns {object} - JSX for the button.
   */
  renderOrderButton(type, options) {
    const {
      handleOrderClick,
      sortDirection,
      sortField,
    } = this.props;

    const asc = type === sortField && 1 === sortDirection;
    const desc = type === sortField && 1 > sortDirection;
    const leftAlign = 'strategy' === type;

    return (
      <div className="grant-item__sort">
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label className="grant-item__sort-label" htmlFor="grant-items-order">
          {type}
        </label>
        <select
          aria-label={type}
          className={classNames(
            'grant-item__details order',
            {
              leftAlign,
              asc,
              desc,
            }
          )}
          id="grant-items-order"
          onChange={(e) => handleOrderClick(e.target.value)}
        >
          {options.map(({ label, value }) => (
            <option
              value={value}
            >
              {label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  /**
   * Renders component markup.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      displayCount,
      grants,
      grantTypesTaxonomy,
      increment,
      loadMore,
      programAreasTaxonomy,
      sortField,
      strategiesTaxonomy,
      statesTaxonomy,
    } = this.props;

    return (
      <Fragment>
        <div className="grant-listing__grants-header">
          <p className="grant-listing__count">
            <span className="grant-listing__count-number">{`${grants.length} `}</span>
            {`${__('Found', 'kauffman')} `}
            {1 === grants.length
              ? __('Grant', 'kauffman')
              : __('Grants', 'kauffman')}
          </p>
          <div className="grant-listing__sorts">
            {this.renderSortButton(
              'Sort By',
              [{
                label: 'Organization',
                value: 'organization',
              }, {
                label: 'Amount',
                value: 'amount',
              }, {
                label: 'Year',
                value: 'date',
              }]
            )}
            {this.renderOrderButton(
              'Order By',
              [{
                label: 'organization' === sortField ? 'A → Z' : 'Ascending',
                value: 'asc',
              }, {
                label: 'organization' === sortField ? 'Z → A' : 'Descending',
                value: 'desc',
              }]
            )}
          </div>
        </div>
        <div className="grant-listing__grants">
          {0 === grants.length && (
            <div className="grant-listing__loading">
              {__('No grants match the provided filters.', 'kauffman')}
            </div>
          )}
          {0 < grants.length && (
            <Fragment>
              <ul className="block__cards">
                {grants.slice(0, displayCount).map((grant) => (
                  <GrantsListItem
                    key={grant.id}
                    grant={grant}
                    grantTypesTaxonomy={grantTypesTaxonomy}
                    programAreasTaxonomy={programAreasTaxonomy}
                    strategiesTaxonomy={strategiesTaxonomy}
                    statesTaxonomy={statesTaxonomy}
                    type={grant}
                  />
                ))}
              </ul>
              {(displayCount < grants.length) &&
              (
                <div className="grant-listing__button-container">
                  <button
                    className="grant-listing__load-more-button"
                    onClick={loadMore}
                  >
                    {__('Load More', 'kauffman')}
                  </button>
                  <div className="loadMoreMsg">
                    {sprintf(
                      __('Display %d more grants of %d.'),
                      increment,
                      grants.length
                    )}
                  </div>
                </div>
              )
              }
            </Fragment>
          )}
        </div>
      </Fragment>
    );
  }
}

GrantsList.propTypes = {
  displayCount: PropTypes.number.isRequired,
  grants: PropTypes.arrayOf(PropTypes.shape(GRANT_SHAPE)).isRequired,
  grantTypesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  handleSortClick: PropTypes.func.isRequired,
  handleOrderClick: PropTypes.func.isRequired,
  increment: PropTypes.number.isRequired,
  loadMore: PropTypes.func.isRequired,
  programAreasTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  sortDirection: PropTypes.number.isRequired,
  sortField: PropTypes.string.isRequired,
  strategiesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
  statesTaxonomy: PropTypes.objectOf(PropTypes.shape(TAXONOMY_MAP))
    .isRequired,
};
